/**
 * Black Marble — INTRO CINEMATIC clock. A dedicated fixed-step accumulator clock,
 * fully decoupled from `simTime`, that every cinematic envelope (camera segment,
 * body rotation, mote drift, grain, audio cue) reads as the SINGLE source of time.
 *
 * Root-cause smoothness fix: the prior build drove off the raw rAF timestamp
 * clamped to [0,30] with no dt clamp, so a 56 MB GLB decode stall jumped cinematic
 * time ~800 ms forward and warped the motion. Here a hard `DT_CLAMP` ceiling means a
 * decode/GC hitch *pauses* cinematic time instead of teleporting it, and the fixed
 * `DT = 1/60` accumulator integrates easing at a constant rate independent of the
 * display refresh (120 Hz does not speed it up; a 50 Hz frame does not judder it).
 */
export class CineClock {
  /** Cinematic seconds — the ONLY time the beats read. */
  t = 0;
  private last = 0;
  private acc = 0;
  /** Fixed integration step. */
  static readonly DT = 1 / 60;
  /**
   * Hitch ceiling — a GC / decode stall cannot teleport motion. 250 ms (not the
   * original 50 ms): under occluded-window rAF throttling (~1 fps) a 50 ms clamp
   * compounded into a ~20× slow-motion film; 250 ms still absorbs any real decode
   * hitch while letting a throttled tab track wall-clock closely. True pausing on
   * a HIDDEN tab is handled explicitly in tick().
   */
  static readonly DT_CLAMP = 0.25;

  /** Advance the clock from a monotonic millisecond timestamp (rAF `now`). */
  tick(nowMs: number): void {
    // Pause-on-hidden: cinematic time holds while the document is hidden (the
    // film resumes where it paused instead of crawling or jumping on return).
    if (typeof document !== 'undefined' && document.hidden) {
      this.last = nowMs;
      return;
    }
    if (!this.last) {
      this.last = nowMs;
      return;
    }
    let frame = (nowMs - this.last) / 1000;
    this.last = nowMs;
    frame = Math.min(frame, CineClock.DT_CLAMP); // CLAMP — no teleport on hitch
    this.acc += frame;
    // EPS guards the fixed-step boundary: a frame measured as ms (e.g. 1000/60)
    // can land a sub-femtosecond below DT after the subtraction, which would
    // otherwise swallow a whole step on a perfectly-paced 60 fps frame.
    while (this.acc >= CineClock.DT - 1e-9) {
      this.t += CineClock.DT;
      this.acc -= CineClock.DT;
    }
  }

  /** Reset to t = 0 with a fresh timestamp baseline (idempotent replays). */
  reset(): void {
    this.t = 0;
    this.last = 0;
    this.acc = 0;
  }
}
